import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'motion/react'
import { Badge, Accordion, Button } from '@/shared/ui'
import { useEffectivePermissions } from '@/auth/useEffectivePermissions'
import { formatMicroEur, formatTokens } from './botUtils'
import { renderBotText } from './botMarkdown'
import { useCancelAnswer, useBotSettings } from './hooks/useBot'
import { swatchFor, DEFAULT_PROVIDER_SWATCH } from './botColors'
import { useBotStore } from './botStore'
import type { BotAnswer } from './api/botApi'

// Badge tone per bot.Answer.status (CK_BotAnswer_status). Kept as a literal
// map (not imported from Badge — its Tone union isn't exported) so this
// stays structurally assignable to Badge's `tone` prop.
const STATUS_TONE = {
  queued: 'neutral',
  running: 'info',
  done: 'success',
  failed: 'danger',
  cancelled: 'neutral',
} as const

function BlinkingCaret() {
  const reduced = useReducedMotion()
  if (reduced) {
    return <span className="inline-block w-[2px] h-[1em] bg-current align-middle ml-0.5" aria-hidden="true" />
  }
  return (
    <motion.span
      className="inline-block w-[2px] h-[1em] bg-current align-middle ml-0.5"
      animate={{ opacity: [1, 0, 1] }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      aria-hidden="true"
    />
  )
}

interface AnswerCardProps {
  answer: BotAnswer
}

/**
 * A single model's answer inside a turn. Data surface — flat per
 * DESIGN.md §14 (never GlassCard here; glass is chrome-only).
 */
export const AnswerCard = memo(function AnswerCard({ answer }: AnswerCardProps) {
  const { t } = useTranslation('bot')
  const { isAdmin } = useEffectivePermissions()
  const cancelAnswer = useCancelAnswer()
  const [cancelError, setCancelError] = useState<string | null>(null)
  const { data: settings } = useBotSettings()

  const cancellable = answer.status === 'queued' || answer.status === 'running'
  // Per-PROVIDER (not per-model) chat color, picked in Settings › Chat-Farben
  // — falls back to DEFAULT_PROVIDER_SWATCH until the user (or migration
  // 264) has set one, and to a neutral tone for any unrecognized swatch id
  // (swatchFor's own fallback).
  const setSelectedModels = useBotStore((st) => st.setSelectedModels)
  const swatchId = settings?.providerColors?.[answer.provider] ?? DEFAULT_PROVIDER_SWATCH[answer.provider]
  const swatch = swatchFor(swatchId)

  function handleCancel() {
    setCancelError(null)
    cancelAnswer.mutate(answer.answerId, {
      onError: () => setCancelError(t('errors.generic')),
    })
  }

  return (
    // Full border + very light bg tint from the provider's swatch (never a
    // side stripe — design ban). `border` alone (no *-outline-variant) since
    // the inline style is what actually paints the color; the class only
    // supplies the 1px width/solid style.
    <div className="rounded-2xl border p-4 space-y-3" style={{ borderColor: swatch.border, backgroundColor: swatch.bg }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: swatch.dot }} aria-hidden="true" />
          <span className="text-sm font-semibold text-on-surface truncate">{answer.modelLabel}</span>
          {answer.status !== 'done' && (
            <Badge tone={STATUS_TONE[answer.status]}>{t(`status.${answer.status}`)}</Badge>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-on-surface-variant tabular-nums">
          {answer.usage && (
            <span>
              {t('answer.tokensIn')} {formatTokens(answer.usage.tokensIn)} · {t('answer.tokensOut')}{' '}
              {formatTokens(answer.usage.tokensOut)}
            </span>
          )}
          <span>{formatMicroEur(answer.usage?.costMicroEur ?? null, answer.usage?.costStatus ?? 'unknown', t)}</span>
          {/* Owner ask ("how do I reply to them separately?"): one click scopes
              the composer's model selection to just this card's model — each
              model keeps its own thread server-side (buildThread filters on
              model_id_fk), so the next message lands only here. */}
          {(answer.status === 'done' || answer.status === 'failed') && (
            <Button
              variant="ghost"
              size="sm"
              icon="reply"
              aria-label={t('answer.replyOnly')}
              title={t('answer.replyOnly')}
              onClick={() => setSelectedModels([answer.modelId])}
            />
          )}
        </div>
      </div>

      {answer.status === 'failed' && answer.error && (
        <p role="alert" className="text-sm text-error">{answer.error}</p>
      )}

      {answer.content && (
        // aria-live is "off" while streaming: useBotTurn/useBotConversation
        // poll every 1.5s whenever the answer is non-terminal, so a
        // "polite" region here would have a screen reader re-announce the
        // growing content on every tick for as long as the answer runs.
        // Only mark it live once the answer is terminal, for a single
        // completion announcement — the toast.answered copy (fired once
        // from BotWidget's TurnWatcher) already covers completion when the
        // panel is closed, so this is for the panel-open case.
        //
        // <div>, not <p>: renderBotText can emit a <ul> for "- "/"· "
        // bullet lines, which is invalid inside a <p>.
        <div
          aria-live={answer.status === 'running' ? 'off' : 'polite'}
          aria-atomic="false"
          className="text-sm text-on-surface whitespace-pre-wrap"
        >
          {renderBotText(answer.content)}
          {answer.status === 'running' && <BlinkingCaret />}
        </div>
      )}

      {cancelError && <p role="alert" className="text-xs text-error">{cancelError}</p>}

      {cancellable && (
        <Button variant="ghost" size="sm" onClick={handleCancel} loading={cancelAnswer.isPending}>
          {t('answer.cancel')}
        </Button>
      )}

      {/* Admin-only: non-technical Verwaltung staff never see tool/SQL
          internals (owner directive — "100% dummy friendly"). Gated on
          useEffectivePermissions().isAdmin, not a role string, so it tracks
          the same IT.Admin-sees-everything rule the rest of the app uses.
          Design Spec §9 lists "Als CSV" for the last result table alongside
          this disclosure. Deferred: BotToolCall only carries
          {seq,tool,sqlText,rowCount,durationMs,error} — no row-level data —
          so a real export isn't implementable against the current API
          surface. Revisit once the backend exposes result rows. */}
      {isAdmin && answer.toolCalls.length > 0 && (
        <Accordion
          items={[
            {
              id: 'sql',
              header: (
                <span className="text-xs font-medium text-on-surface-variant">
                  {t('answer.techDetails', { count: answer.toolCalls.length })}
                </span>
              ),
              content: (
                <div className="space-y-3">
                  {answer.toolCalls.map((tc) => (
                    <div key={tc.seq} className="space-y-1">
                      <div className="text-[11px] text-on-surface-variant">
                        {tc.tool}
                        {tc.rowCount != null && ` · ${t('answer.rows', { count: tc.rowCount })}`}
                        {tc.durationMs != null && ` · ${t('answer.duration', { ms: tc.durationMs })}`}
                      </div>
                      {tc.sqlText && (
                        <pre className="text-xs overflow-x-auto bg-surface-container-low rounded-lg p-2">{tc.sqlText}</pre>
                      )}
                      {tc.error && <div className="text-xs text-error">{tc.error}</div>}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  )
})
